import type { Renderer2 } from '@angular/core';
import { Injectable, inject } from '@angular/core';
import { SvgService, cloneSvg } from '@freelancer/svg-service';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Service for fetching animated SVGs.
 * Implementation is similar to IconService, but with some differences:
 * - The SVG bypasses zone.js by using the non-patched functions.
 * - The SVG is expected to contain a single `<script>` which contains the animation.
 * - The `<script>` contains the animation logic.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class AnimatedSvgService {
  /**
   * List of functions that need to be bypassed by the animation script.
   *
   * If you've encountered the`ScriptTimeoutError`. This is a good place to start looking.
   * The animated SVG may be accessing an unlisted asynchronous function that is patched by zone.js.
   */
  private static readonly zoneBypassReplacements: [RegExp, string][] = [
    [
      /window\.requestAnimationFrame/g,
      'window.__zone_symbol__requestAnimationFrame',
    ],
    [
      /window\.cancelAnimationFrame/g,
      'window.__zone_symbol__cancelAnimationFrame',
    ],
    [/window\.setTimeout/g, 'window.__zone_symbol__setTimeout'],
    [/window\.clearTimeout/g, 'window.__zone_symbol__clearTimeout'],
  ];

  private svgService = inject(SvgService);

  getAnimatedSvg(url: string, renderer: Renderer2): Observable<Element> {
    const cachedAnimation = this.svgService.getCacheSvg(url);
    if (cachedAnimation) {
      return of(cloneSvg(cachedAnimation));
    }
    return this.svgService.fetchSvg(url).pipe(
      map(markup => {
        const svgElement = this.svgService.parseSvgMarkup(markup, renderer);
        const animatedSvgElement = this.preprocessSvgScript(svgElement);
        this.svgService.setCacheSvg(url, animatedSvgElement);
        return cloneSvg(animatedSvgElement);
      }),
    );
  }

  /**
   * Replaces window functions with their bypassed equivalents.
   * @param svgElement Element to modify the script
   * @returns The provided svgElement but with the script modified in place.
   */
  private preprocessSvgScript(svgElement: SVGElement): SVGElement {
    const scriptElement = svgElement.querySelector(
      'script',
    ) as HTMLScriptElement;
    if (!scriptElement?.textContent) {
      return svgElement;
    }

    scriptElement.textContent =
      AnimatedSvgService.zoneBypassReplacements.reduce(
        (script, [zoneValue, bypassedZoneValue]) =>
          script.replace(zoneValue, bypassedZoneValue),
        scriptElement.textContent,
      );
    return svgElement;
  }
}
